import React from 'react';
import { CreditCard, Smartphone, HandCoins } from 'lucide-react';
import { PaymentMethod } from '../../types';

interface PaymentChoiceProps {
    methods: PaymentMethod[];
    value: PaymentMethod;
    onChange: (method: PaymentMethod) => void;
    sellerFirstName: string;
}

const META: Record<PaymentMethod, { icon: React.ReactNode; title: (seller: string) => string; sub: (seller: string) => string }> = {
    STRIPE: {
        icon: <CreditCard size={16} />,
        title: () => 'Betala nu i kassan',
        sub: () => 'Kort, Swish, Apple Pay eller Google Pay. Klart direkt.',
    },
    SWISH: {
        icon: <Smartphone size={16} />,
        title: seller => `Swisha ${seller}`,
        sub: () => 'Du får numret och en QR-kod när du beställt',
    },
    SELLER: {
        icon: <HandCoins size={16} />,
        title: seller => `Betala ${seller} direkt`,
        sub: () => 'När ni ses, eller som ni kommer överens',
    },
};

/**
 * Hur kunden betalar när kaffet hämtas hos säljaren. Visar bara de sätt
 * admin slagit på för gruppen. Med ett enda sätt visas bara en rad text.
 */
export const PaymentChoice: React.FC<PaymentChoiceProps> = ({ methods, value, onChange, sellerFirstName }) => {
    if (methods.length <= 1) {
        const only = methods[0] || 'SELLER';
        return (
            <p className="mb-6 px-1 text-[11px] text-stone-500 leading-snug">
                <span className="font-bold text-stone-700">Betalning:</span> {META[only].title(sellerFirstName)}. {META[only].sub(sellerFirstName)}.
            </p>
        );
    }
    return (
        <fieldset className="mb-6">
            <legend className="text-xs font-black uppercase tracking-widest text-stone-400 mb-3 px-1">Hur vill du betala?</legend>
            <div className="space-y-2">
                {methods.map(method => {
                    const selected = value === method;
                    const meta = META[method];
                    return (
                        <label
                            key={method}
                            className={`flex items-center gap-3 p-3.5 rounded-xl border transition-colors cursor-pointer focus-within:ring-2 focus-within:ring-coffee-500/30 ${selected ? 'bg-coffee-50 border-coffee-300 ring-1 ring-coffee-300' : 'bg-white border-stone-200 hover:border-coffee-200'}`}
                        >
                            <input type="radio" name="payment-method" value={method} checked={selected} onChange={() => onChange(method)} className="sr-only" />
                            <span className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${selected ? 'bg-coffee-900 text-white' : 'bg-stone-100 text-stone-500'}`}>
                                {meta.icon}
                            </span>
                            <span className="flex-1 min-w-0">
                                <span className="block text-sm font-bold text-stone-800">{meta.title(sellerFirstName)}</span>
                                <span className="block text-[11px] text-stone-500 leading-snug mt-0.5">{meta.sub(sellerFirstName)}</span>
                            </span>
                        </label>
                    );
                })}
            </div>
        </fieldset>
    );
};
